import React, { FC } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';

import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';

import { Media } from './media';

interface ImagePreviewModalProps {
  open: boolean;
  onClose: () => void;
  image: Media;
  altText: string;
  isVideo: boolean;
  onDelete: () => void;
}

const ImagePreviewModal: FC<ImagePreviewModalProps> = ({
  open,
  onClose,
  image,
  altText,
  isVideo,
  onDelete,
}) => {
  const downloadHref = image.download_url || image.url;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = downloadHref;
    link.download = `${isVideo ? 'video' : 'image'}-${image.id || Date.now()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'grey.900',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'white',
          pb: 1,
        }}
      >
        {altText}
        <IconButton aria-label="close" onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
          p: 2,
        }}
      >
        {isVideo ? (
          <Box
            component="video"
            controls
            sx={{
              maxWidth: '100%',
              maxHeight: '70vh',
              width: 'auto',
              height: 'auto',
            }}
          >
            <source src={image.url} type={image.content_type || 'video/mp4'} />
            Your browser does not support the video tag.
          </Box>
        ) : (
          <Box
            component="img"
            src={image.url}
            alt={altText}
            sx={{
              maxWidth: '100%',
              maxHeight: '70vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button
          onClick={onDelete}
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          size="large"
        >
          Delete {isVideo ? 'Video' : 'Image'}
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          size="large"
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImagePreviewModal;
